import React, { MouseEvent, useContext, useMemo } from 'react';
import pluralize from 'pluralize';
import { ReactComponent as PathIcon } from 'assets/paths-icon.svg';
import { ReactComponent as TrashIcon } from 'assets/trash-icon.svg';
import WidgetModel from '@mm/state/model/WidgetModel';
import ButtonModel, {
  ButtonType,
  PageButtonConfig,
} from '@mm/state/model/ButtonModel';
import './TextButton.pcss';
import { CreateEditButtonArea } from '../CreateEditButtonArea/CreateEditButtonArea';
import { useButtonHandlers } from '../../../helpers/widget-helpers/use-button-handlers';
import { PathGraphContext } from '../../common/PathGraph/PathGraph';
import { getDeviceInfo } from '../../../../common/hooks/use-device-info';
import { PrimaryToolTip } from '../../../components/PrimaryToolTip/PrimaryToolTip';
import classNames from 'classnames';

type Props = {
  widget: WidgetModel;
  button: ButtonModel;
  dropdownOpen: boolean;
  tooltip?: string;
  forAds?: boolean;
  setSelectedButton: (selectedButton: number | undefined) => void;
  showCreatePath?: boolean;
  openedButtonId: number | undefined;
  setOpenedButtonId: (openedButtonId: number | undefined) => void;
  chatbotId: number;
  disabledButtonTypes?: ButtonType[];
  usedInSlideout?: boolean;
};

const getClickThroughString = (button: ButtonModel) => {
  const { clickThrough, type } = button;
  const clicks = button.clicksCount || 0;

  if (type === ButtonType.URL) {
    return `${clicks} ${pluralize('click', clicks)}`;
  }

  return clickThrough || clickThrough === 0
    ? clickThrough === 'N/A'
      ? clickThrough
      : `${clickThrough}%`
    : null;
};

export const TextButton = (props: Props) => {
  const {
    widget,
    button,
    forAds,
    tooltip,
    setOpenedButtonId,
    openedButtonId,
    chatbotId,
    disabledButtonTypes,
    usedInSlideout,
  } = props;

  const { setActiveNode, deselectWidgetButtons, removeNodeAndSuccessors } =
    useContext(PathGraphContext);

  const disabled = useMemo(
    () => disabledButtonTypes?.includes(button.type),
    [disabledButtonTypes, button.type],
  );

  const buttonHandlers = useButtonHandlers();
  const { pageId } = button.config as PageButtonConfig;
  const clickThroughString = getClickThroughString(button);
  const { isMobile } = getDeviceInfo();

  const onClickDelete = (e: MouseEvent<HTMLOrSVGElement>) => {
    if (disabled) {
      return;
    }
    e.stopPropagation();
    buttonHandlers.delete(widget.id, widget.pageId, button);
    removeNodeAndSuccessors?.({
      id: button.id,
      type: 'button',
      actionType: 'delete',
      parentId: widget.id,
      parentType: 'widget',
    });
  };

  const onClickButton = () => {
    if (disabled) {
      return;
    }

    setOpenedButtonId(button.id);

    deselectWidgetButtons?.(widget.buttons);

    if (button.type === ButtonType.PAGE) {
      setActiveNode?.({
        id: button.id,
        type: 'button',
        dialogueId: (button.config as PageButtonConfig).pageId,
        action: 'open',
      });
    }
  };

  const { viewsCount, type } = button;

  const viewCountString =
    type === ButtonType.CALL ? '' : `/${viewsCount ?? 'N/A'}`;

  const component = (
    <div
      className={classNames('text-button', {
        'text-button--disabled': disabled,
      })}
    >
      {openedButtonId === button.id && (
        <CreateEditButtonArea
          disabledButtonTypes={disabledButtonTypes}
          button={button}
          widget={widget}
          forAds={forAds}
          closeButtonArea={() => setOpenedButtonId(undefined)}
          chatbotId={chatbotId}
          usedInSlideout={usedInSlideout}
        />
      )}

      <div onClick={onClickButton} className="text-button__button">
        <div className="text-button__center-wrapper">
          {pageId && <PathIcon className="text-button__path-icon" />}
          <span className="text-button__button-text">{button.name}</span>
        </div>

        <div className="text-button__stats">
          {!isMobile && clickThroughString && (
            <div className="text-button__click-through">
              {clickThroughString}
              {viewCountString}
            </div>
          )}
        </div>

        <TrashIcon
          className="text-button__trash-icon"
          onClick={onClickDelete}
        />
      </div>
    </div>
  );

  return tooltip ? (
    <PrimaryToolTip useHover={true} anchorElement={component}>
      {tooltip}
    </PrimaryToolTip>
  ) : (
    component
  );
};
